import jwt_decode from 'jwt-decode';
import {AuthState} from 'acscp/store';

const roleSchemaUri = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
interface JwtProperties {
    role?: string[] | string;
    [roleSchemaUri]?: string[] | string;
}

export enum AuthRole {
    Charters = 'cpCharters',
    Loyalty = 'cpLoyalty',
}

const rolesFromToken = (token: string): string[] => {
    const decodedJwt = jwt_decode<JwtProperties>(token, {header: false});
    const roleField = decodedJwt.role ?? decodedJwt[roleSchemaUri];
    if (roleField instanceof Array) {
        return roleField;
    }
    if (typeof roleField === 'string') {
        return [roleField];
    }
    return [];
};

const userTokenHasRole = (token: string|undefined, role: AuthRole): boolean =>
    token !== undefined && rolesFromToken(token).some(tokenRole => tokenRole === role);

export const userIsLoggedIn = (authState: AuthState): boolean =>
    !!authState.refreshToken;

export const userIsAuthenticated = (authState: AuthState): boolean =>
    userIsLoggedIn(authState) || authState.sharingTokens.length > 0;

/**
 * Whether the user can access the charters functionality. Note that a user may only have a sharing token and not
 * be allowed to request new flights.
 */
export const userCanAccessCharters = (authState: AuthState): boolean =>
    userTokenHasRole(authState.token, AuthRole.Charters) ||
    (!authState.isNonJetPortalUser && authState.sharingTokens.length > 0);

export const userCanAccessLoyalty = (authState: AuthState): boolean =>
    userTokenHasRole(authState.token, AuthRole.Loyalty);

/**
 * Whether the user is logged in, but only with the Loyalty auth role. Unlike userCanAccessCharters this is false if
 * charters access is only because of a sharing token.
 */
export const userCanOnlyAccessLoyalty = (authState: AuthState): boolean =>
    userIsLoggedIn(authState) && userCanAccessLoyalty(authState) && !userTokenHasRole(authState.token, AuthRole.Charters);

export const userCanRequestFlights = (authState: AuthState): boolean =>
    userIsLoggedIn(authState) && userTokenHasRole(authState.token, AuthRole.Charters) && FLIGHT_REQUESTS_ENABLED;

/**
 * Whether the user can potentially request flights because they already can request flights or they may be able
 * when they are logged in.
 */
export const userCanPotentiallyRequestFlights = (authState: AuthState): boolean =>
    userCanRequestFlights(authState) ||
    (ENABLE_CREATE_ACCOUNT && FLIGHT_REQUESTS_ENABLED && !userIsLoggedIn(authState) && !authState.isNonJetPortalUser);
